// ═══════════════════════════════════════════════════════════════
// MarkdownToolbar.js — Barre de formatage réutilisable (Session J+1)
// ═══════════════════════════════════════════════════════════════
// Boutons d'insertion Markdown autour de la sélection / position curseur.
// Réutilisable : MailingComposer, atelier Écrivain (S57-G), édition coffre.
//
// Usage :
//   <MarkdownToolbar
//     textareaRef={taRef}
//     value={bodyMd}
//     onChange={setBodyMd}
//     palette={palette}
//   />
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import {
    Bold, Italic, Heading1, Heading2, Heading3,
    List, ListOrdered, Link as LinkIcon, Quote, Minus, Image as ImageIcon, X, Table as TableIcon,
} from 'lucide-react';

// ── Helpers d'insertion ─────────────────────────────────────────
// Wrap toggle : si la sélection est déjà entourée par before/after, on les
// retire (toggle off). Sinon on wrappe normalement.
// Couvre 2 cas d'entourage :
//   1. EXTERNE : ambient texte autour de la sélection contient déjà before/after
//      ex. valeur = "*hello*", sélection = "hello" → toggle off → "hello"
//   2. INTERNE : la sélection elle-même commence par before et finit par after
//      ex. valeur = "*hello*", sélection = "*hello*" → toggle off → "hello"
function wrapSelection(textarea, before, after = before, placeholder = '') {
    if (!textarea) return null;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selected = value.slice(start, end);

    // Toggle off externe
    if (selected.length > 0) {
        const beforeAmbient = value.slice(Math.max(0, start - before.length), start);
        const afterAmbient = value.slice(end, end + after.length);
        if (beforeAmbient === before && afterAmbient === after) {
            const newValue = value.slice(0, start - before.length) + selected + value.slice(end + after.length);
            return { newValue, selectionStart: start - before.length, selectionEnd: end - before.length };
        }
    }

    // Toggle off interne
    if (selected.length >= before.length + after.length
        && selected.startsWith(before)
        && selected.endsWith(after)) {
        const inner = selected.slice(before.length, selected.length - after.length);
        const newValue = value.slice(0, start) + inner + value.slice(end);
        return { newValue, selectionStart: start, selectionEnd: start + inner.length };
    }

    // Wrap normal
    const inner = selected || placeholder;
    const newValue = value.slice(0, start) + before + inner + after + value.slice(end);
    const newSelStart = start + before.length;
    const newSelEnd = newSelStart + inner.length;
    return { newValue, selectionStart: newSelStart, selectionEnd: newSelEnd };
}

// Insérer en début de ligne (titres, listes, citation).
function prependLine(textarea, prefix) {
    if (!textarea) return null;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    // Si déjà préfixé identique → retirer (toggle).
    const currentLine = value.slice(lineStart, value.indexOf('\n', start) === -1 ? value.length : value.indexOf('\n', start));
    if (currentLine.startsWith(prefix)) {
        const newValue = value.slice(0, lineStart) + currentLine.slice(prefix.length) + value.slice(lineStart + currentLine.length);
        return { newValue, selectionStart: start - prefix.length, selectionEnd: end - prefix.length };
    }
    const newValue = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    return { newValue, selectionStart: start + prefix.length, selectionEnd: end + prefix.length };
}

// Insertion d'un squelette de tableau markdown GFM (rows × cols).
function insertTableSnippet(textarea, rows, cols) {
    if (!textarea) return null;
    const r = Math.max(1, Math.min(20, parseInt(rows, 10) || 2));
    const c = Math.max(1, Math.min(8, parseInt(cols, 10) || 2));
    const headerRow = '| ' + Array.from({ length: c }, (_, i) => `Colonne ${i + 1}`).join(' | ') + ' |';
    const sepRow = '|' + Array.from({ length: c }, () => ' --- ').join('|') + '|';
    const dataRows = Array.from({ length: r }, () =>
        '| ' + Array.from({ length: c }, () => '   ').join(' | ') + ' |'
    ).join('\n');
    const table = `${headerRow}\n${sepRow}\n${dataRows}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const before = (start > 0 && value[start - 1] !== '\n') ? '\n\n' : '';
    const after = (end < value.length && value[end] !== '\n') ? '\n\n' : '\n';
    const snippet = `${before}${table}${after}`;
    const newValue = value.slice(0, start) + snippet + value.slice(end);
    const newPos = start + snippet.length;
    return { newValue, selectionStart: newPos, selectionEnd: newPos };
}

// Insertion brute à la position du curseur (séparateur).
function insertAtCursor(textarea, snippet) {
    if (!textarea) return null;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const newValue = value.slice(0, start) + snippet + value.slice(end);
    const newPos = start + snippet.length;
    return { newValue, selectionStart: newPos, selectionEnd: newPos };
}

// Lien : si sélection = URL → wrapper en [texte](url), sinon [sélection](url).
function insertLink(textarea) {
    if (!textarea) return null;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selected = value.slice(start, end);
    const looksLikeUrl = /^https?:\/\//i.test(selected.trim());
    const text = looksLikeUrl ? 'texte du lien' : (selected || 'texte du lien');
    const url = looksLikeUrl ? selected.trim() : 'https://exemple.com';
    const snippet = `[${text}](${url})`;
    const newValue = value.slice(0, start) + snippet + value.slice(end);
    // Sélectionne la partie texte ou url selon ce qui doit être édité.
    const selStart = start + 1;
    const selEnd = start + 1 + text.length;
    return { newValue, selectionStart: selStart, selectionEnd: selEnd };
}

// Insertion image markdown : `![alt](url){width=X align=Y}` à la position du curseur.
// - Auto-upgrade http://→https:// pour mixed-content
// - Suffixe {width=...} optionnel : 25/50/75/100 (= %) ou 'auto'
// - Suffixe align=... optionnel : left/center/right (defaut center)
// Si width=auto + align=center → markdown propre `![alt](url)` (sans suffixe).
function insertImageSnippet(textarea, url, alt = '', width = 'auto', align = 'center') {
    if (!textarea || !url) return null;
    let cleanUrl = String(url).trim();
    if (/^http:\/\//i.test(cleanUrl)) cleanUrl = cleanUrl.replace(/^http:\/\//i, 'https://');
    const cleanAlt = String(alt || '').trim();
    const isDefault = (width === 'auto' || !width) && (align === 'center' || !align);

    let snippet;
    if (isDefault) {
        snippet = `![${cleanAlt}](${cleanUrl})`;
    } else {
        const parts = [];
        if (width && width !== 'auto') parts.push(`width=${width}`);
        if (align && align !== 'center') parts.push(`align=${align}`);
        snippet = `![${cleanAlt}](${cleanUrl}){${parts.join(' ')}}`;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const before = (start > 0 && value[start - 1] !== '\n') ? '\n\n' : '';
    const after = (end < value.length && value[end] !== '\n') ? '\n\n' : '\n';
    const finalSnippet = `${before}${snippet}${after}`;
    const newValue = value.slice(0, start) + finalSnippet + value.slice(end);
    const newPos = start + finalSnippet.length;
    return { newValue, selectionStart: newPos, selectionEnd: newPos };
}

// ── Hook réutilisable ───────────────────────────────────────────
export function useTextareaInsert(textareaRef, onChange) {
    return React.useCallback((mutator) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const result = mutator(ta);
        if (!result) return;
        onChange(result.newValue);
        // Restore selection après le re-render React.
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(result.selectionStart, result.selectionEnd);
        });
    }, [textareaRef, onChange]);
}

// ── Composant ───────────────────────────────────────────────────
function MarkdownToolbar({ textareaRef, onChange, palette, compact = false }) {
    const apply = useTextareaInsert(textareaRef, onChange);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [tableModalOpen, setTableModalOpen] = useState(false);

    const buttons = [
        { icon: Bold, label: 'Gras (Ctrl+B)', action: (ta) => wrapSelection(ta, '**', '**', 'gras') },
        { icon: Italic, label: 'Italique (Ctrl+I)', action: (ta) => wrapSelection(ta, '*', '*', 'italique') },
        { sep: true },
        { icon: Heading1, label: 'Titre 1', action: (ta) => prependLine(ta, '# ') },
        { icon: Heading2, label: 'Titre 2', action: (ta) => prependLine(ta, '## ') },
        { icon: Heading3, label: 'Titre 3', action: (ta) => prependLine(ta, '### ') },
        { sep: true },
        { icon: List, label: 'Liste à puces', action: (ta) => prependLine(ta, '- ') },
        { icon: ListOrdered, label: 'Liste numérotée', action: (ta) => prependLine(ta, '1. ') },
        { icon: Quote, label: 'Citation', action: (ta) => prependLine(ta, '> ') },
        { sep: true },
        { icon: LinkIcon, label: 'Lien', action: insertLink },
        { icon: ImageIcon, label: 'Insérer une image', action: () => { setImageModalOpen(true); return null; } },
        { icon: TableIcon, label: 'Insérer un tableau', action: () => { setTableModalOpen(true); return null; } },
        { icon: Minus, label: 'Séparateur horizontal', action: (ta) => insertAtCursor(ta, '\n\n---\n\n') },
    ];

    const btnStyle = {
        background: 'transparent',
        color: palette?.text || '#1a1a2a',
        border: `1px solid ${palette?.border || '#e6e6ec'}`,
        borderRadius: 6,
        padding: compact ? '5px 7px' : '6px 9px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 120ms ease',
    };

    const sepStyle = {
        width: 1,
        alignSelf: 'stretch',
        background: palette?.border || '#e6e6ec',
        margin: '0 4px',
    };

    return (
        <>
        <div
            role="toolbar"
            aria-label="Mise en forme Markdown"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: compact ? '6px 8px' : '8px 10px',
                background: palette?.panel || '#ffffff',
                border: `1px solid ${palette?.border || '#e6e6ec'}`,
                borderRadius: 8,
                flexWrap: 'wrap',
            }}
        >
            {buttons.map((b, i) => {
                if (b.sep) return <div key={`sep-${i}`} style={sepStyle} aria-hidden="true" />;
                const Icon = b.icon;
                return (
                    <button
                        key={b.label}
                        type="button"
                        title={b.label}
                        aria-label={b.label}
                        onClick={() => apply(b.action)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = palette?.accentSoft || '#f3eeff';
                            e.currentTarget.style.borderColor = palette?.accent || '#8b5cf6';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = palette?.border || '#e6e6ec';
                        }}
                        style={btnStyle}
                    >
                        <Icon size={compact ? 13 : 14} />
                    </button>
                );
            })}
        </div>
        {imageModalOpen && (
            <ImageInsertModal
                palette={palette}
                onClose={() => setImageModalOpen(false)}
                onConfirm={(url, alt, width, align) => {
                    setImageModalOpen(false);
                    apply((ta) => insertImageSnippet(ta, url, alt, width, align));
                }}
            />
        )}
        {tableModalOpen && (
            <TableInsertModal
                palette={palette}
                onClose={() => setTableModalOpen(false)}
                onConfirm={(rows, cols) => {
                    setTableModalOpen(false);
                    apply((ta) => insertTableSnippet(ta, rows, cols));
                }}
            />
        )}
        </>
    );
}

// ── Modale d'insertion de tableau ──────────────────────────────
function TableInsertModal({ palette, onClose, onConfirm }) {
    const [rows, setRows] = useState(3);
    const [cols, setCols] = useState(3);
    const accent = palette?.accent || '#a78bfa';
    const accentBorder = 'rgba(167,139,250,0.30)';
    const accentSoft = 'rgba(167,139,250,0.08)';
    const text = palette?.text || '#1a1a2a';
    const muted = palette?.muted || '#888';
    const border = palette?.border || '#e6e6ec';
    const cardBg = palette?.panel || palette?.cardBg || '#ffffff';

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9200, padding: 20,
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: cardBg, color: text,
                border: `1px solid ${border}`, borderRadius: 14,
                width: '100%', maxWidth: 420, padding: 22,
                boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
            }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: accentSoft,
                            border: `1px solid ${accentBorder}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <TableIcon size={15} style={{ color: accent }} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: text }}>Insérer un tableau</h3>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: muted, padding: 4 }}>
                        <X size={16} />
                    </button>
                </header>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Lignes</span>
                            <input
                                type="number" min={1} max={20}
                                value={rows}
                                onChange={(e) => setRows(parseInt(e.target.value, 10) || 1)}
                                style={{
                                    background: cardBg, color: text,
                                    border: `1px solid ${border}`, borderRadius: 8,
                                    padding: '9px 12px', fontSize: 14,
                                    outline: 'none', width: '100%', boxSizing: 'border-box',
                                }}
                            />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Colonnes</span>
                            <input
                                type="number" min={1} max={8}
                                value={cols}
                                onChange={(e) => setCols(parseInt(e.target.value, 10) || 1)}
                                style={{
                                    background: cardBg, color: text,
                                    border: `1px solid ${border}`, borderRadius: 8,
                                    padding: '9px 12px', fontSize: 14,
                                    outline: 'none', width: '100%', boxSizing: 'border-box',
                                }}
                            />
                        </label>
                    </div>

                    {/* Mini-aperçu grille */}
                    <div style={{
                        background: accentSoft, border: `1px solid ${accentBorder}`,
                        borderRadius: 8, padding: 10,
                        display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3,
                        maxHeight: 150, overflow: 'hidden',
                    }}>
                        {Array.from({ length: cols }, (_, ci) => (
                            <div key={`h${ci}`} style={{ height: 18, background: accent, opacity: 0.5, borderRadius: 2 }} />
                        ))}
                        {Array.from({ length: rows * cols }, (_, i) => (
                            <div key={i} style={{ height: 14, background: 'rgba(167,139,250,0.18)', borderRadius: 2 }} />
                        ))}
                    </div>

                    <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.5 }}>
                        Tableau markdown GFM inséré avec en-tête. Tu pourras éditer le contenu directement dans la zone de texte.
                    </div>
                </div>

                <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent', color: muted,
                            border: `1px solid ${border}`, borderRadius: 6,
                            padding: '8px 14px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirm(rows, cols)}
                        style={{
                            background: accentSoft, color: accent,
                            border: `1px solid ${accentBorder}`, borderRadius: 6,
                            padding: '8px 14px', fontSize: 12.5, fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <TableIcon size={13} /> Insérer
                    </button>
                </footer>
            </div>
        </div>
    );
}

// ── Modale d'insertion d'image ──────────────────────────────────
function ImageInsertModal({ palette, onClose, onConfirm }) {
    const [url, setUrl] = useState('');
    const [alt, setAlt] = useState('');
    const [width, setWidth] = useState('auto'); // auto | 25 | 50 | 75 | 100
    const [align, setAlign] = useState('center'); // left | center | right
    const [error, setError] = useState('');

    const accent = palette?.accent || '#a78bfa';
    const accentBorder = 'rgba(167,139,250,0.30)';
    const accentSoft = 'rgba(167,139,250,0.08)';
    const text = palette?.text || '#1a1a2a';
    const muted = palette?.muted || '#888';
    const border = palette?.border || '#e6e6ec';
    const cardBg = palette?.panel || palette?.cardBg || '#ffffff';

    const submit = () => {
        const cleanUrl = url.trim();
        if (!cleanUrl) {
            setError('URL requise');
            return;
        }
        if (!/^https?:\/\//i.test(cleanUrl)) {
            setError('L\'URL doit commencer par http:// ou https://');
            return;
        }
        // L'insert convertit http→https automatiquement, on passe l'URL telle quelle
        onConfirm(cleanUrl, alt.trim(), width, align);
    };

    const willUpgrade = /^http:\/\//i.test(url.trim());

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9200, padding: 20,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: cardBg, color: text,
                    border: `1px solid ${border}`, borderRadius: 14,
                    width: '100%', maxWidth: 480, padding: 22,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
                }}
            >
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: accentSoft,
                            border: `1px solid ${accentBorder}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <ImageIcon size={15} style={{ color: accent }} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: text }}>Insérer une image</h3>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: muted, padding: 4 }}>
                        <X size={16} />
                    </button>
                </header>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>URL de l'image</span>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => { setUrl(e.target.value); setError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                            placeholder="https://exemple.com/photo.jpg"
                            autoFocus
                            style={{
                                background: cardBg, color: text,
                                border: `1px solid ${error ? '#ef4444' : border}`, borderRadius: 8,
                                padding: '9px 12px', fontSize: 13.5,
                                fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box',
                            }}
                        />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Texte alternatif (optionnel)</span>
                        <input
                            value={alt}
                            onChange={(e) => setAlt(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                            placeholder="Description de l'image pour l'accessibilité"
                            style={{
                                background: cardBg, color: text,
                                border: `1px solid ${border}`, borderRadius: 8,
                                padding: '9px 12px', fontSize: 13.5,
                                fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box',
                            }}
                        />
                    </label>
                    {error && (
                        <div style={{ fontSize: 12, color: '#ef4444', padding: '6px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6 }}>
                            {error}
                        </div>
                    )}
                    {willUpgrade && !error && (
                        <div style={{ fontSize: 11.5, color: muted, padding: '6px 10px', background: accentSoft, border: `1px solid ${accentBorder}`, borderRadius: 6 }}>
                            L'URL sera convertie automatiquement en HTTPS pour assurer le chargement dans la newsletter.
                        </div>
                    )}

                    {/* Largeur + Alignement — grid 2 colonnes premium */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Largeur</span>
                            <div style={{ display: 'flex', gap: 4, padding: 3, background: accentSoft, border: `1px solid ${border}`, borderRadius: 6 }}>
                                {[
                                    { v: 'auto', label: 'Auto' },
                                    { v: '25', label: '25%' },
                                    { v: '50', label: '50%' },
                                    { v: '75', label: '75%' },
                                    { v: '100', label: '100%' },
                                ].map(o => (
                                    <button
                                        key={o.v}
                                        onClick={() => setWidth(o.v)}
                                        type="button"
                                        style={{
                                            flex: 1,
                                            background: width === o.v ? accent : 'transparent',
                                            color: width === o.v ? '#fff' : muted,
                                            border: 'none', borderRadius: 4,
                                            padding: '5px 0', fontSize: 11, fontWeight: 600,
                                            cursor: 'pointer', transition: 'all 0.12s',
                                        }}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Alignement</span>
                            <div style={{ display: 'flex', gap: 4, padding: 3, background: accentSoft, border: `1px solid ${border}`, borderRadius: 6 }}>
                                {[
                                    { v: 'left', label: '← Gauche' },
                                    { v: 'center', label: '↔ Centre' },
                                    { v: 'right', label: 'Droite →' },
                                ].map(o => (
                                    <button
                                        key={o.v}
                                        onClick={() => setAlign(o.v)}
                                        type="button"
                                        style={{
                                            flex: 1,
                                            background: align === o.v ? accent : 'transparent',
                                            color: align === o.v ? '#fff' : muted,
                                            border: 'none', borderRadius: 4,
                                            padding: '5px 0', fontSize: 11, fontWeight: 600,
                                            cursor: 'pointer', transition: 'all 0.12s',
                                        }}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    {url.trim() && /^https?:\/\//i.test(url.trim()) && !error && (
                        <div style={{
                            background: accentSoft, border: `1px solid ${accentBorder}`,
                            borderRadius: 8, padding: 6, textAlign: 'center', maxHeight: 180, overflow: 'hidden',
                        }}>
                            <img
                                src={url.trim()}
                                alt={alt}
                                style={{ maxWidth: '100%', maxHeight: 168, borderRadius: 4 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.5 }}>
                        L'image doit être hébergée publiquement (URL accessible sans login).
                        Hébergeurs courants : Imgur, Cloudinary, GitHub raw, ton CDN.
                    </div>
                </div>

                <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent', color: muted,
                            border: `1px solid ${border}`, borderRadius: 6,
                            padding: '8px 14px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={!url.trim()}
                        style={{
                            background: accentSoft, color: accent,
                            border: `1px solid ${accentBorder}`, borderRadius: 6,
                            padding: '8px 14px', fontSize: 12.5, fontWeight: 600,
                            cursor: url.trim() ? 'pointer' : 'not-allowed',
                            opacity: url.trim() ? 1 : 0.5,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <ImageIcon size={13} /> Insérer
                    </button>
                </footer>
            </div>
        </div>
    );
}

export default MarkdownToolbar;
